import { Link } from 'react-router-dom';
import { WorqlyMark } from '../design-system/components/WorqlyMark';

/**
 * MarketingFooter
 * --------------------------------------------------------------
 * Multi-column mega-footer used by every marketing page.
 *   - Column 1: brand + tagline
 *   - Columns 2–4: Product / Company / Legal
 *   - Bottom row: copyright + status pill + security link + NaqviX credit
 *
 * Internal paths use react-router <Link>. External links use <a>
 * with the proper rel attributes.
 */

type FooterLink = { label: string; to?: string; href?: string };
type FooterCol = { title: string; links: FooterLink[] };

const COLS: FooterCol[] = [
  {
    title: 'Product',
    links: [
      { label: 'Features',     to: '/features' },
      { label: 'Pricing',      to: '/pricing' },
      { label: 'Integrations', to: '/integrations' },
      { label: 'Docs',          to: '/docs' },
      { label: 'Changelog',    to: '/changelog' },
      { label: 'Start free',   to: '/signup' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About',     to: '/about' },
      { label: 'Customers', to: '/customers' },
      { label: 'Security',  to: '/security' },
      { label: 'Status',    to: '/status' },
      { label: 'Contact',   to: '/contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms of service', to: '/legal/terms' },
      { label: 'Privacy policy',   to: '/legal/privacy' },
      { label: 'DPA',              to: '/legal/dpa' },
      { label: 'Acceptable use',   to: '/legal/aup' },
    ],
  },
];


export function MarketingFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="mt-24 bg-ink-900 text-white dark:bg-black dark:border-t dark:border-white/5"
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>

      <div className="max-w-site mx-auto px-6 lg:px-8">
        {/* Oversized wordmark band */}
        <div className="pt-16 pb-10 border-b border-white/10">
          <p
            aria-hidden
            className="select-none text-[clamp(64px,12vw,176px)] font-serif italic font-light tracking-[-0.05em] leading-[0.95] text-white/85"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.25) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            worqly.
          </p>
          <p className="mt-4 text-[14px] text-white/55 max-w-md">
            The operating system for small business — invoicing, expenses, HR, payroll, time tracking, projects, payments.
          </p>
        </div>

        {/* Sitemap grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12 py-14">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2" aria-label="Worqly home">
              <WorqlyMark size="md" variant="on-dark" />
            </Link>
            <p className="mt-4 text-[13.5px] leading-relaxed text-white/60 max-w-xs">
              Modular business platform — pay for the modules you use and the employees you actually track.
            </p>
          </div>

          {/* Link columns */}
          {COLS.map((col) => (
            <div key={col.title}>
              <h3 className="text-[11px] uppercase tracking-[0.16em] text-white font-bold mb-4">
                {col.title}
              </h3>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.to ? (
                      <Link
                        to={l.to}
                        className="text-[13.5px] text-white/60 hover:text-white transition-colors"
                      >
                        {l.label}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        className="text-[13.5px] text-white/60 hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[12px] text-white/50">© {year} Worqly. All rights reserved.</p>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-white/55">
            <Link to="/status" className="inline-flex items-center gap-1.5 hover:text-white">
              <span className="size-1.5 rounded-full bg-lime-neon" />
              All systems normal
            </Link>
            <span className="text-white/35">·</span>
            <Link to="/security" className="hover:text-white">Security &amp; compliance</Link>
            <span className="text-white/35">·</span>
            <span>
              Designed &amp; developed by{' '}
              <a
                href="https://www.naqvix.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/75 hover:text-white underline-offset-2 hover:underline transition-colors"
              >
                NaqviX
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
